import { Balance, OrderBook, TokenInfo, TokenStats, Trade } from '../types';

// Pure data types - no IO dependencies
export interface AssetWebSocketConfig {
    mint: string;
    baseUrl?: string;
    reconnectAttempts?: number;
    reconnectDelay?: number;
}

export interface AssetWebSocketCallbacks {
    onOrderBookUpdate?: (orderBook: OrderBook) => void;
    onTradeUpdate?: (trade: Trade) => void;
    onBalanceUpdate?: (balance: Balance) => void;
    onTokenStatsUpdate?: (stats: TokenStats) => void;
    onConnectionChange?: (connected: boolean) => void;
    onError?: (error: Error) => void;
}

export interface WebSocketMessage {
    type: 'orderbook_update' | 'trade_update' | 'balance_update' | 'token_stats';
    asset: TokenInfo;
    data?: OrderBook;
    trade?: Trade;
    balance?: Balance;
    stats?: TokenStats;
}

// Pure business logic - testable without WebSocket
export class AssetWebSocketLogic {
    private config: AssetWebSocketConfig;
    private callbacks: AssetWebSocketCallbacks;
    private isConnected: boolean = false;
    private reconnectAttempts: number = 0;

    constructor(config: AssetWebSocketConfig, callbacks: AssetWebSocketCallbacks) {
        this.config = config;
        this.callbacks = callbacks;
    }

    // Business logic methods - pure functions
    getWebSocketUrl(): string {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        let host = this.config.baseUrl?.replace(/^wss?:\/\//, '') || window.location.host;

        // DEV ONLY: When running React dev server on localhost (any port except 3001),
        // connect WebSocket directly to backend on port 3001.
        // In production, frontend is served from the same origin as the API, so this is skipped.
        const isLocalDev = host.startsWith('localhost:') && !host.endsWith(':3001');
        if (isLocalDev) {
            host = 'localhost:3001';
        }

        return `${protocol}//${host}/ws/${this.config.mint}`;
    }

    shouldReconnect(): boolean {
        return this.reconnectAttempts < (this.config.reconnectAttempts || 5);
    }

    getReconnectDelay(): number {
        return (this.config.reconnectDelay || 1000) * this.reconnectAttempts;
    }

    handleConnectionOpen(): void {
        console.log(`WebSocket connected for asset: ${this.config.mint}`);
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.callbacks.onConnectionChange?.(true);
    }

    handleConnectionClose(): void {
        console.log(`WebSocket disconnected for asset: ${this.config.mint}`);
        this.isConnected = false;
        this.callbacks.onConnectionChange?.(false);
    }

    handleConnectionError(error: Event): void {
        console.error(`WebSocket error for asset ${this.config.mint}:`, error);
        this.callbacks.onError?.(new Error(`WebSocket error for ${this.config.mint}`));
    }

    handleMessage(data: string): void {
        try {
            const message: WebSocketMessage = JSON.parse(data);

            // Validate that the message is for our asset
            if (message.asset.mint !== this.config.mint) {
                console.warn(`Received message for wrong asset. Expected: ${this.config.mint}, Got: ${message.asset.mint}`);
                return;
            }

            switch (message.type) {
                case 'orderbook_update':
                    if (message.data) {
                        this.callbacks.onOrderBookUpdate?.(message.data);
                    }
                    break;
                case 'trade_update':
                    if (message.trade) {
                        this.callbacks.onTradeUpdate?.(message.trade);
                    }
                    break;
		case 'balance_update':
		    if (message.balance) {
			this.callbacks.onBalanceUpdate?.(message.balance);
		    }
		    break;
		case 'token_stats':
		    if (message.stats) {
			this.callbacks.onTokenStatsUpdate?.(message.stats);
		    }
		    break;
                default:
                    console.log(`Unknown message type for ${this.config.mint}:`, message);
            }
        } catch (error) {
            console.error(`Error parsing WebSocket message for ${this.config.mint}:`, error);
            this.callbacks.onError?.(error instanceof Error ? error : new Error('Parse error'));
        }
    }

    incrementReconnectAttempts(): void {
        this.reconnectAttempts++;
    }

    resetReconnectAttempts(): void {
        this.reconnectAttempts = 0;
    }

    isCurrentlyConnected(): boolean {
        return this.isConnected;
    }

    getAsset(): string {
        return this.config.mint;
    }
}

// IO layer - handles actual WebSocket communication
export class AssetWebSocketService {
    private logic: AssetWebSocketLogic;
    private ws: WebSocket | null = null;
    private reconnectTimeout: NodeJS.Timeout | null = null;

    constructor(config: AssetWebSocketConfig, callbacks: AssetWebSocketCallbacks) {
        this.logic = new AssetWebSocketLogic(config, callbacks);
    }

    async connect(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const url = this.logic.getWebSocketUrl();
                console.log(`Connecting to per-asset WebSocket: ${url}`);
                
                this.ws = new WebSocket(url);

                this.ws.onopen = () => {
                    this.logic.handleConnectionOpen();
                    resolve();
                };

                this.ws.onclose = () => {
                    this.logic.handleConnectionClose();
                    this.attemptReconnect();
                };

                this.ws.onerror = (error) => {
                    this.logic.handleConnectionError(error);
                    reject(error);
                };

                this.ws.onmessage = (event) => {
                    this.logic.handleMessage(event.data);
                };

            } catch (error) {
                reject(error);
            }
        });
    }

    disconnect(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }

    isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    getAsset(): string {
        return this.logic.getAsset();
    }

    private attemptReconnect(): void {
        if (this.logic.shouldReconnect()) {
            this.logic.incrementReconnectAttempts();
            const delay = this.logic.getReconnectDelay();
            
            console.log(`Attempting to reconnect to ${this.logic.getAsset()} in ${delay}ms (attempt ${this.logic['reconnectAttempts']})`);
            
            this.reconnectTimeout = setTimeout(() => {
                this.connect().catch(error => {
                    console.error(`Reconnection failed for ${this.logic.getAsset()}:`, error);
                });
            }, delay);
        } else {
            console.error(`Max reconnection attempts reached for ${this.logic.getAsset()}`);
        }
    }
} 
